import assign from 'lodash/assign';
import map from 'lodash/map';
import {
  listingUtils_getCondensedModelsAndPricing,
  listingUtils_getIconType,
  listingUtils_isApartmentBldg,
  listingUtils_userOnWaitlist,
} from 'app/shared/utils/listingUtils';
import { filterUtils_getSearchSlugFromListing } from 'app/shared/utils/filterUtils';
import dateUtils from 'app/shared/utils/dateUtils';

import Address from 'app/shared/models/Address';
import Broker from 'app/shared/models/Broker';
import Amenities from 'app/shared/models/Amenities';
import Contact from 'app/shared/models/Contact';
import Details from 'app/shared/models/Details';
import FloorplanModel from 'app/shared/models/FloorplanModel';
import Geo from 'app/shared/models/Geo';
import History from 'app/shared/models/History';
import ListedBy from 'app/shared/models/ListedBy';
import ListingAreas from 'app/shared/models/ListingAreas';
import ModelCollection from 'app/shared/models/ModelCollection';
import OpenHouse from 'app/shared/models/OpenHouse';
import Photo from 'app/shared/models/Photo';
import PopularitySummary from 'app/shared/models/PopularitySummary';
import Pricing from 'app/shared/models/Pricing';
import RecencyTime from 'app/shared/models/RecencyTime';
import School from 'app/shared/models/School';
import UserItemDates from 'app/shared/models/UserItemDates';
import { RoomDetails } from 'app/shared/models/RoomForRent';

import noPhoto from 'images/noPhoto.png';
import { logError } from '@zg-rentals/log-error';
import type { FloorplanModelProps } from 'app/shared/models/FloorplanModel';
import type { OpenHouseParams } from 'app/shared/models/OpenHouse';
import type { SchoolProps } from 'app/shared/models/School';
import type { HistoryProps } from 'app/shared/models/History';
import type { ListingAreasApiResponse } from 'app/types/areas.type';
import type { ListingMinMaxPriceBeds, Model } from 'app/types/commonTypes.type';
import type { InstantTourProviders, TotalMonthlyPriceDisplayInfo } from 'app/types/listingDetails.type';

interface ListingPhoto {
  large?: { url?: string };
  medium?: { url?: string };
  squareThumb?: { url?: string };
  caption?: string;
}

interface DetailsParam {
  mlsLogo?: string;
  disclaimer?: string;
  landlordPhotoUrl?: string;
  specialOffers?: Array<{
    label?: string;
    description?: string;
  }>;
  trusted?: boolean;
  isBrokerExclusiveListing?: boolean;
  isBrokerListing?: boolean;
  isAllowedToRetain?: boolean;
  featuredPropertyProductType?: string;
  roomForRent?: {
    roomDetails?: {
      roomSqFt?: number;
      utilitiesShareDescription?: string;
      roomIsFurnished?: boolean;
      hasPrivateBath?: boolean;
    };
  } | null;
  instantTourProviders?: InstantTourProviders | null;
}

interface ListedByParam {
  contactPhone?: string;
  memberPhone?: string;
  contactName?: string;
  webSite?: string;
  companyName?: string;
}

interface CompanyProfileParam {
  contactPhone?: string;
}

interface PopularityParam {
  summary?: {
    from?: { date?: number; ago?: string };
    favorites?: number;
    viewed?: number;
    inquiries?: number;
  };
}

interface ZestimateParam {
  zestimate?: number;
}

interface ContactParam {
  defaultInquiryMessage?: string;
}

interface AddressParam {
  hideStreet?: boolean;
}

interface UnitV2FirstParam {
  active?: boolean;
  address?: AddressParam;
  aliasEncoded?: string;
  amenities?: Amenities;
  companyProfile?: CompanyProfileParam;
  contact?: ContactParam;
  created?: number;
  details?: DetailsParam;
  featuredPropertyProductId?: number;
  floorplans?: Array<FloorplanModelProps>;
  history?: HistoryProps;
  isPaidAndNotDemoted?: boolean;
  isPaidMultifamily?: boolean;
  legalDisclaimer?: string;
  listedBy?: ListedByParam;
  listingPhotos?: Array<ListingPhoto>;
  listingType?: string;
  maloneLotIdEncoded?: string;
  medPhotoUrls?: Array<string>;
  models?: Array<Model>;
  openHouses?: Array<OpenHouseParams>;
  photoCount?: number;
  popularity?: PopularityParam;
  pricing?: {
    Studio?: { priceLow?: number; priceHigh?: number; bedsLow?: number; bedsHigh?: number };
    1?: { priceLow?: number; priceHigh?: number; bedsLow?: number; bedsHigh?: number };
    2?: { priceLow?: number; priceHigh?: number; bedsLow?: number; bedsHigh?: number };
    3?: { priceLow?: number; priceHigh?: number; bedsLow?: number; bedsHigh?: number };
    ThreePlus?: { priceLow?: number; priceHigh?: number; bedsLow?: number; bedsHigh?: number };
  };
  pricingStats?: {
    priceLow?: number;
    priceHigh?: number;
    bedsLow?: number;
    bedsHigh?: number;
  };
  priority?: number;
  propertyType?: string;
  rentalApplicationStatus?: string;
  searchKeyword?: string;
  thumbPhotoUrl?: string;
  title?: string;
  unit?: string;
  updated?: number;
  uriBuilding?: string;
  uriMalone?: string;
  userActivity?: {
    waitlist?: { hasJoined?: boolean };
  };
  userItems?: {
    inquiry?: { time?: number };
    favorite?: { time?: number };
    hidden?: { time?: number };
    viewed?: { time?: number };
  };
  userItemTypes?: Array<string>;
  waitlisted?: boolean;
  zestimate?: ZestimateParam;
  hasTotalMonthlyPrice?: boolean;
  totalMonthlyPriceDisplayInfo?: TotalMonthlyPriceDisplayInfo;
}

interface UnitV2SecondParam {
  areas?: ListingAreasApiResponse;
  geo?: {
    lat?: number;
    lon?: number;
  };
  belongsToMultipleUnitBuilding?: boolean;
  forRentUnitsInBuilding?: Array<string>;
  schools?: Array<SchoolProps>;
}

export interface UnitV2Type {
  active: boolean;
  address: Address;
  aliasEncoded: string;
  amenities: Amenities;
  areas: ReturnType<typeof ListingAreas>;
  belongsToMultipleUnitBuilding: boolean;
  building: boolean;
  contact: Contact;
  created: number;
  details: ReturnType<typeof Details>;
  displayName: string;
  featuredPropertyProductId: number;
  featuredPropertyProductType: string;
  floorplans: Array<FloorplanModel>;
  forRentUnitsInBuilding: Array<string>;
  geo: Geo;
  history: History;
  iconType: string;
  isApartmentBldg: boolean;
  isPaidAndNotDemoted: boolean;
  isPaidMultifamily: boolean;
  isUnit: boolean;
  legalDisclaimer: string;
  largePhotos: Array<Photo>;
  listedBy: ListedBy;
  listingMinMaxPriceBeds: ListingMinMaxPriceBeds;
  listingType: string;
  maloneLotIdEncoded: string;
  medPhotoUrls: Array<string>;
  modelsAndPricing: Array<unknown>;
  openHouses: Array<OpenHouse>;
  photoCount: number;
  photos: Array<Photo>;
  popularity: { summary: PopularitySummary };
  previewPhoto: Photo;
  pricing: Pricing;
  priority: number;
  propertyType: string;
  recencyTime: RecencyTime;
  rentalApplicationStatus: string;
  schools: Array<School>;
  score: number;
  searchKeyword: string;
  showPricingStats: boolean;
  specialOffers: Array<unknown>;
  thumbPhotos: Array<Photo>;
  trusted: boolean;
  unit: string;
  updated: number;
  uriBuilding: string;
  uriMalone: string;
  urlMaloneUnit: string;
  uriV2: string;
  userItemDates: UserItemDates;
  userItemTypes: Array<string>;
  userOnWaitlist: boolean;
  waitlisted: boolean;
  zestimate: number;
  broker: Broker;
  isAllowedToRetain: boolean;
  hasTotalMonthlyPrice: boolean;
  totalMonthlyPriceDisplayInfo: TotalMonthlyPriceDisplayInfo | object;
}

const UnitV2 = function (
  this: UnitV2Type,
  {
    active = true,
    address = {},
    aliasEncoded = '',
    amenities = {} as never,
    companyProfile = {},
    contact = {},
    created = 0,
    details = {},
    featuredPropertyProductId = 0,
    floorplans = [],
    history = {},
    isPaidAndNotDemoted = false,
    isPaidMultifamily = false,
    legalDisclaimer = '',
    listedBy = {},
    listingPhotos = [],
    listingType = '',
    maloneLotIdEncoded = '',
    medPhotoUrls = [], // HPWEB-6082: Medium photo URLs for new listing cards
    models = [],
    openHouses = [],
    photoCount = 0,
    popularity = {},
    pricing = {},
    pricingStats = {},
    priority = 0,
    propertyType = '',
    rentalApplicationStatus = '',
    searchKeyword = '',
    thumbPhotoUrl = noPhoto,
    title = '',
    unit = '',
    updated = 0,
    uriBuilding = '',
    uriMalone = '',
    userActivity = {},
    userItems = {},
    userItemTypes = [],
    waitlisted = false,
    zestimate = {},
    hasTotalMonthlyPrice = false,
    totalMonthlyPriceDisplayInfo = {} as TotalMonthlyPriceDisplayInfo,
  }: UnitV2FirstParam = {},
  {
    areas = {},
    geo = {},
    belongsToMultipleUnitBuilding = false,
    forRentUnitsInBuilding = [],
    schools = [],
  }: UnitV2SecondParam = {},
): void | false {
  try {
    listingType = listingType.toLowerCase();
    propertyType = propertyType.toLowerCase();

    const searchSlug = filterUtils_getSearchSlugFromListing({ listingType, propertyType } as {
      listingType: string;
      propertyType: string;
    });
    // @ts-expect-error - ListingAreas is a constructor function without proper typing
    const processedAreas = new ListingAreas({ searchSlug, areas, pricingStats, pricing });
    const modelCollection = ModelCollection();
    /*
     * `floorplans` for /listing/byMaloneLotIdEncodedV2
     * `models` for all other calls
     */
    modelCollection.add(floorplans.length ? floorplans : models, listingType, propertyType);
    const showPricingStats = pricingStats ? true : false;
    const neighborhood = (areas.specific && areas.specific.name) || '';
    this.active = Boolean(active === false ? false : true);
    const addressInstance = new Address(assign(address, { neighborhood }));
    this.address = addressInstance;
    this.aliasEncoded = String(aliasEncoded);
    // Amenities from API doesn't match AmenitiesParams exactly, use type assertion
    this.amenities = new Amenities(amenities as never, new RoomDetails(details?.roomForRent?.roomDetails ?? {}));
    this.areas = processedAreas;
    this.belongsToMultipleUnitBuilding = Boolean(belongsToMultipleUnitBuilding);
    this.building = false;

    // HP-5674: Strip street address from defaultInquiryMessage received via API if needed.
    if (addressInstance.hideStreet) {
      contact.defaultInquiryMessage = `I'd like to schedule a viewing for this listing. Please contact me with more information!`;
    }

    this.contact = new Contact(contact);
    this.created = Number(created); // does the api return the `activated` value as `created`?
    // @ts-expect-error - Details is a constructor function without proper typing
    this.details = new Details(details, floorplans);
    this.displayName = String(title);
    this.featuredPropertyProductId = Number(featuredPropertyProductId);
    this.featuredPropertyProductType = String((details && details.featuredPropertyProductType) || '');
    this.floorplans = map(floorplans, (floorplan) => {
      return new FloorplanModel(floorplan);
    });
    this.forRentUnitsInBuilding = map(forRentUnitsInBuilding, (unitInBuilding) => {
      return String(unitInBuilding);
    });
    this.geo = new Geo(geo);
    this.history = new History(history);
    this.iconType = String(listingUtils_getIconType(propertyType));
    this.isApartmentBldg = Boolean(listingUtils_isApartmentBldg(listingType, propertyType));
    this.isPaidAndNotDemoted = Boolean(isPaidAndNotDemoted);
    this.isPaidMultifamily = Boolean(isPaidMultifamily);
    this.isUnit = true;
    this.legalDisclaimer = String(legalDisclaimer);
    this.largePhotos = map(listingPhotos, (image = {}) => {
      return new Photo({
        url: image.large && image.large.url,
        caption: image.caption,
      });
    });
    // Sanitize PII for inactive listings to prevent exposure in preloaded state
    const sanitizedListedBy = active
      ? assign({}, listedBy, {
          mlsLogo: details.mlsLogo,
          disclaimer: details.disclaimer,
          landlordPhotoUrl: details.landlordPhotoUrl,
          contactPhone: listedBy.contactPhone || companyProfile.contactPhone || null,
        })
      : {
          mlsLogo: details.mlsLogo,
          disclaimer: details.disclaimer,
          // Sanitize PII fields for inactive listings
          contactName: undefined,
          contactPhone: undefined,
          memberPhone: undefined,
          landlordPhotoUrl: undefined,
          webSite: undefined,
          companyName: undefined,
        };

    this.listedBy = new ListedBy(sanitizedListedBy);
    this.listingMinMaxPriceBeds = modelCollection.summarize();
    this.listingType = String(listingType);
    this.maloneLotIdEncoded = String(maloneLotIdEncoded);

    // HPWEB-6082: Medium photo URLs for new listing cards
    this.medPhotoUrls =
      medPhotoUrls ||
      listingPhotos
        ?.filter((image) => {
          return image?.medium?.url;
        })
        .map((image) => {
          return image.medium?.url || '';
        });

    // @ts-expect-error - listingUtils_getCondensedModelsAndPricing has never[] default param
    this.modelsAndPricing = listingUtils_getCondensedModelsAndPricing(floorplans);
    this.openHouses = map(openHouses, (openHouseEvent) => {
      return new OpenHouse(openHouseEvent);
    });
    this.photoCount = Number(photoCount);
    this.photos = map(listingPhotos, (image = {}) => {
      return new Photo({
        url: image.medium && image.medium.url,
        caption: image.caption,
      });
    });
    this.popularity = {
      summary: new PopularitySummary(popularity.summary),
    };
    this.previewPhoto =
      thumbPhotoUrl === noPhoto ? new Photo({ url: noPhoto, noPhoto: true }) : new Photo({ url: thumbPhotoUrl });
    this.pricing = new Pricing(pricing);
    this.priority = Number(priority);
    this.propertyType = String(propertyType);
    this.recencyTime = new RecencyTime(dateUtils.recencyTime(created, new Date()));
    this.rentalApplicationStatus = String(rentalApplicationStatus);
    this.schools = map(schools, (school: SchoolProps) => {
      const schoolWithSlug = { ...school, searchSlug };
      return new School(schoolWithSlug);
    });
    this.score = Number(priority);
    this.searchKeyword = String(searchKeyword);
    this.showPricingStats = showPricingStats;
    this.specialOffers = details.specialOffers || [];
    this.thumbPhotos = map(listingPhotos, (image = {}) => {
      return new Photo({
        url: image.squareThumb && image.squareThumb.url,
        caption: image.caption,
      });
    });
    this.trusted = Boolean(details.trusted);
    this.unit = String(unit); // Unit A
    this.updated = Number(updated);
    this.uriBuilding = String(decodeURI(uriBuilding));
    this.uriMalone = String(decodeURI(uriMalone));
    this.urlMaloneUnit = String(unit).toLowerCase().replace(' ', '-'); // 'unit-a'
    this.uriV2 = String(decodeURI(uriMalone));
    this.userItemDates = new UserItemDates(userItems);
    this.userItemTypes = map(userItemTypes, (userItem) => {
      return String(userItem || '');
    });
    this.userOnWaitlist = listingUtils_userOnWaitlist(userActivity);
    this.waitlisted = Boolean(waitlisted);
    this.zestimate = Number(zestimate.zestimate || 0);
    this.broker = new Broker(
      Object.assign(
        {
          isBrokerExclusiveListing: details.isBrokerExclusiveListing,
          isBrokerListing: details.isBrokerListing,
        },
        sanitizedListedBy, // Use sanitized data instead of original listedBy
      ),
    );
    this.isAllowedToRetain = Boolean(details.isAllowedToRetain);
    this.hasTotalMonthlyPrice = Boolean(hasTotalMonthlyPrice);
    this.totalMonthlyPriceDisplayInfo = totalMonthlyPriceDisplayInfo || {};
  } catch (err) {
    logError({ error: err, errorType: 'DataModelError', context: 'UnitV2' });
    return false;
  }
};

export default UnitV2;
